// MenuDisplayer.tsx
import React, { useState } from 'react';
import CategoryDisplayer from './components/CategoryDisplayer/CategoryDisplayer';
import './MenuDisplayer.scss';
import { HiOutlinePlus, HiOutlineTrash, HiOutlinePencilSquare } from 'react-icons/hi2';
import CreateMenuModal from './components/CreateMenuModal/CreateMenuModal';
import UpdateMenuModal from './components/UpdateMenuModal/UpdateMenuModal';
import DeleteMenuModal from './components/DeleteMenuModal/DeleteMenuModal';
import { getPermissionState } from 'utils/functions';
import { toggleMenuState } from '../../../../../../crud/menu_api/menus';
import { toast } from 'react-toastify';
import { MenuCategoryNode, MenuType } from '../../types/menu.types';
import { calculateMenuStatus } from '../../utils/menu';

interface MenuDisplayerProps {
  menus: MenuType[];
  setMenus: (menus: MenuType[]) => void;
  selectedMenu: MenuType | null;
  setSelectedMenu: (menu: MenuType | null) => void;
  refreshMenus: () => void;
}

const MenuDisplayer: React.FC<MenuDisplayerProps> = ({
  menus,
  setMenus,
  selectedMenu,
  setSelectedMenu,
  refreshMenus
}) => {
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showUpdateMenu, setShowUpdateMenu] = useState(false);
  const [showDeleteMenu, setShowDeleteMenu] = useState(false);
  const [dynamicallySelectedMenus, setDynamicallySelectedMenus] = useState<MenuType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<MenuCategoryNode | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<MenuCategoryNode | null>(null);
  const [initialCategoryId, setInitialCategoryId] = useState<string | undefined>(undefined);
  const [menuForModal, setMenuForModal] = useState<MenuType | null>(null);

  function noOpenModals() {
    return !showCreateMenu && !showUpdateMenu && !showDeleteMenu;
  }

  const handleClickMenu = (menu: MenuType) => {
    setSelectedMenu(menu);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setInitialCategoryId(undefined);
    setDynamicallySelectedMenus([menu]);
  };

  const handleClickCategory = (event: React.MouseEvent, menu: MenuType, category: MenuCategoryNode) => {
    event.stopPropagation();
    setSelectedMenu(menu);
    setDynamicallySelectedMenus([menu]);
    setInitialCategoryId(String(category.id));
    setSelectedCategory(null);
  };

  const handleClickDelete = (menu: MenuType) => {
    setMenuForModal(menu);
    setShowDeleteMenu(true);
  };

  const handleClickUpdate = (menu: MenuType) => {
    setMenuForModal(menu);
    setShowUpdateMenu(true);
  };

  const handleStatusChange = async (menu: MenuType) => {
    const newStatus = !menu.state;

    try {
      await toggleMenuState(menu.menu_id);

      const updatedMenus = menus.map((m) =>
        m.menu_id === menu.menu_id
          ? { ...m, state: newStatus }
          : m
      );
      setMenus(updatedMenus);
      toast.success(`¡Menú ${newStatus ? 'activado' : 'desactivado'} correctamente!`);
    } catch (error) {
      toast.error('Hubo un error al actualizar el estado del menú.');
      console.error('Error cambiando el estado del menú:', error);
    }
  };

  const capitalizeFirstLetter = (text: string) => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const renderMenus = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(320px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-5 pt-4">
        {menus.map((menu, index) => {
          const menuStatus = calculateMenuStatus(menu.schedule || null);
          const isSelected = dynamicallySelectedMenus.includes(menu);
          const isDisabled = menu.state === false;

          return (
            <div
              key={index}
              className={`
                group bg-[var(--card-color)] rounded-xl p-5 cursor-pointer
                border transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]
                hover:shadow-lg hover:border-[#FC7556]
                ${isSelected ? 'border-[#FC7556] shadow-md' : 'border-[var(--border-color)] shadow-sm'}
                ${isDisabled ? 'opacity-50 grayscale-[30%]' : ''}
              `}
              onClick={() => handleClickMenu(menu)}
            >
              {/* Header row: title + schedule + actions */}
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-base font-semibold text-[var(--strong-color)] truncate flex-1 m-0">
                  {capitalizeFirstLetter(menu.menu_name)}
                </h2>

                {menu.schedule && (
                  <span className={`
                    text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0
                    ${menuStatus.isOpen
                      ? 'bg-green-500/10 text-green-500'
                      : 'bg-red-500/10 text-red-500'
                    }
                  `}>
                    {menuStatus.isOpen ? '● Abierto' : '● Cerrado'}
                  </span>
                )}

                <div className="flex items-center gap-2 ml-auto flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Toggle switch */}
                  <label className="menu-toggle-switch">
                    <input
                      type="checkbox"
                      checked={menu.state !== false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStatusChange(menu);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="menu-toggle-slider"></span>
                  </label>

                  {/* Edit */}
                  <button
                    className="mp-btn-icon"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickUpdate(menu);
                    }}
                  >
                    <HiOutlinePencilSquare size={16} />
                  </button>

                  {/* Delete */}
                  <button
                    className="mp-btn-icon mp-btn-icon-danger"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickDelete(menu);
                    }}
                  >
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </div>

              {/* Description */}
              {menu.menu_description && (
                <p className="text-sm text-[var(--text-color)] opacity-60 mb-3 line-clamp-2 leading-relaxed">
                  {menu.menu_description}
                </p>
              )}

              {/* Category pills */}
              <div className="flex flex-wrap gap-1.5">
                {menu.categories.map((mainCategory) => (
                  <button
                    key={mainCategory.id}
                    className="
                      px-2.5 py-1 text-xs font-medium rounded-lg
                      bg-[var(--button-color)] text-[var(--text-color)] border border-[var(--border-color)]
                      transition-all duration-150
                      hover:border-[#FC7556] hover:text-[#FC7556] hover:bg-[rgba(252,117,86,0.06)]
                    "
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickCategory(e, menu, mainCategory);
                    }}
                  >
                    {capitalizeFirstLetter(mainCategory.name)}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {selectedMenu && (
        <h3 className="flex items-center gap-2 text-base font-semibold text-[var(--text-color)] mb-3 ml-1 flex-wrap">
          <span className="text-[#FC7556] cursor-pointer hover:opacity-80 transition-opacity">
            {selectedMenu.menu_name}
          </span>
          {selectedCategory && (
            <>
              <span className="opacity-40">{'>'}</span>
              <span className="text-[#FC7556] cursor-pointer hover:opacity-80 transition-opacity">
                {selectedCategory.name}
              </span>
            </>
          )}
          {selectedSubcategory && (
            <>
              <span className="opacity-40">{'>'}</span>
              <span className="text-[#FC7556] cursor-pointer hover:opacity-80 transition-opacity">
                {selectedSubcategory.name}
              </span>
            </>
          )}
        </h3>
      )}

      {!selectedMenu && (
        <button
          className="mp-btn-primary mb-3"
          onClick={() => setShowCreateMenu(true)}
          disabled={!getPermissionState(16)}
        >
          <HiOutlinePlus size={16} /> Crear Menú
        </button>
      )}

      {(!selectedMenu || !noOpenModals()) && renderMenus()}

      {selectedMenu && noOpenModals() && (
        <CategoryDisplayer
          menus={menus}
          setMenus={setMenus}
          selectedMenu={selectedMenu}
          setSelectedMenu={setSelectedMenu}
          selectedParentCategory={selectedCategory}
          setSelectedParentCategory={setSelectedCategory}
          selectedSubcategory={selectedSubcategory}
          setSelectedSubcategory={setSelectedSubcategory}
          initialCategoryId={initialCategoryId}
          initialSubcategoryId={undefined}
          refreshMenus={refreshMenus}
        />
      )}

      {showCreateMenu && (
        <CreateMenuModal
          show={showCreateMenu}
          handleClose={() => setShowCreateMenu(false)}
          menus={menus}
          setMenus={setMenus}
          refreshMenus={refreshMenus}
        />
      )}

      {showDeleteMenu && menuForModal && (
        <DeleteMenuModal
          show={showDeleteMenu}
          handleClose={() => {
            setShowDeleteMenu(false);
          }}
          menus={menus}
          setMenus={setMenus}
          menu={menuForModal}
          setSelectedMenu={setSelectedMenu}
        />
      )}

      {showUpdateMenu && menuForModal && (
        <UpdateMenuModal
          show={showUpdateMenu}
          handleClose={() => {
            setShowUpdateMenu(false);
          }}
          menus={menus}
          setMenus={setMenus}
          menu={menuForModal}
          setSelectedMenu={setSelectedMenu}
          refreshMenus={refreshMenus}
        />
      )}
    </>
  );
};

export default MenuDisplayer;
